import * as React from 'react'
import { useParams } from 'react-router-dom'
import { BadgeCheck, Loader2, ShieldAlert } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { formatDate } from '@/lib/format'
import { ProgramBadge } from '@/components/program-badge'

/**
 * /badges/:guid — where a badge's QR code lands. Public, signed out.
 *
 * Calls ONE function, `public.certification_badge(uuid)` (0066), which answers
 * only while the record is `complete`. So this page is the badge's proof: if a
 * coach withdraws the approval, every copy of the badge — printed, posted,
 * screenshotted — scans to "could not verify" from then on.
 *
 * Nothing here can be browsed. The only input is the uuid printed in the QR,
 * and a caller cannot ask about a person, only about a badge they were handed.
 */
interface BadgeRow {
  person: string
  certification: string
  certification_slug: string
  summary: string | null
  completed_at: string | null
}

// Checked before calling: a malformed id is a 400 from PostgREST, and that
// should read as "not a badge", not as an outage.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function BadgeVerifyPage() {
  const { guid = '' } = useParams()
  const [row, setRow] = React.useState<BadgeRow | null | undefined>(undefined)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!UUID.test(guid)) {
      setRow(null)
      return
    }
    void (async () => {
      const { data, error: err } = await sb().rpc('certification_badge', { p_badge: guid })
      if (err) setError(err.message)
      else setRow(((data ?? []) as BadgeRow[])[0] ?? null)
    })()
  }, [guid])

  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-xl">
        <header className="mb-8 flex items-center gap-3">
          <img
            src="https://dontpaniclabs.com/wp-content/uploads/2023/06/DPL-full-color-2-o.svg"
            alt="Don't Panic Labs"
            className="h-9"
          />
          <div className="min-w-0">
            <h1 className="text-lg font-semibold leading-tight">Badge verification</h1>
            <p className="text-xs text-muted-foreground">
              Checks a Don’t Panic Labs program badge against the record.
            </p>
          </div>
        </header>

        {error && (
          <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
            {error}
          </p>
        )}

        {row === undefined && !error && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Checking…
          </div>
        )}

        {row === null && (
          <div className="rounded-lg border border-dashed p-8 text-center">
            <ShieldAlert className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium">We could not verify this badge</p>
            <p className="mt-1 text-xs text-muted-foreground">
              The link may be mistyped, or the program is no longer marked complete.
            </p>
          </div>
        )}

        {row && (
          <div className="space-y-5">
            <ProgramBadge
              title={row.certification}
              slug={row.certification_slug}
              completedAt={row.completed_at}
              badgeId={guid}
              className="mx-auto max-w-none"
            />
            <div className="flex gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
              <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0 text-sm">
                <p className="font-semibold">Verified</p>
                <p className="mt-0.5">
                  <b>{row.person}</b> completed <b>{row.certification}</b> on{' '}
                  {formatDate(row.completed_at)}, approved by a Don’t Panic Labs coach.
                </p>
                {row.summary && (
                  <p className="mt-2 text-muted-foreground">{row.summary}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
